<template>
  <v-container fluid>
    <UniversalTable
      :config="tableConfig.tableParams.value"
      :items="displayItems"
      :items-count="displayItemsCount"
      :items-per-page="itemsPerPage"
      :backend-pagination="!useClientPagination"
      :externalCurrentPage="
        useClientPagination
          ? undefined
          : Math.floor(paginationDefaultParams.offset / paginationDefaultParams.limit) + 1
      "
      @onSearch="viewState.handleSearch"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      @removeAllFilters="viewState.clearFilters"
      @onSort="viewState.handleSort"
      @onRequest="viewState.handleRequest"
      @onActionClick="handleIssueBadge"
      @levelCounterFilter="handleLevelCounterFilter"
      :loading="showSkeletonLoading || $loader.isAppLoading"
      :emptyObjectData="{
        singular: 'Badge Holder',
        plural: 'Badge Holders',
        emptyTitle: 'No Badges where issued for this Responsibility',
        emptyMessage:
          'You need at least a Master Badge and some Professionals to fulfill this Responsibility',
        emptyButtonLabel: 'Issue Badge',
      }"
    >
    </UniversalTable>

    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Badge Holders"
      :groupsItems="viewState.state.filters.groupsItems"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="620"
      :defaultSelected="viewState.state.filters.values"
    />
  </v-container>
</template>

<script setup>
import { defineProps, defineEmits, computed, watch, toRef, getCurrentInstance, onMounted, ref } from "vue";
import { useBadgeHolders } from "./composables/useBadgeHolders";
import { useTableConfig } from "./composables/useTableConfig";
import { useBadgeHoldersViewState } from "./composables/useBadgeHoldersViewState";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import { toBadgeLevelFilterNumber } from "@components/utils/normalizeBadgeLevel";

const props = defineProps({
  responsibilityId: {
    type: [Number, String],
    required: true,
  },
  items: {
    type: Array,
    required: true,
    description: "Array of badge holder items to display",
  },
  itemsCount: {
    type: Number,
    default: 0,
    description: "Total count of items for pagination",
  },
  filterLevels: {
    type: Array,
    default: () => [],
    description: "Default filter levels to apply (e.g., ['Coach'] or ['Master'])",
  },
  badgeLevelCounts: {
    type: Object,
    default: () => ({
      apprentice: 0,
      professional: 0,
      coach: 0,
      master: 0,
    }),
    description: "Badge level counts from responsibilityData",
  },
  onRefreshBadges: {
    type: Function,
    default: null,
    description: "Called after expiration / status changes on a badge row (e.g. refetch list).",
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  /**
   * When true, paginate `items` in the table (e.g. Storybook / static data).
   * Production API flows should keep this false and rely on backend pagination.
   */
  useClientPagination: {
    type: Boolean,
    default: false,
  },
  pageType: {
    type: String,
    default: "responsibility",
  },
  hideActionButton: {
    type: Boolean,
    default: false,
  },
  hideBadgeRemoveIcon: {
    type: Boolean,
    default: false,
  },
  /** When true, proficiency expiration icons do not open the date editor (directory drill-down). */
  expirationIconReadOnly: {
    type: Boolean,
    default: false,
  },
  /** True while the badge holders list is being fetched — drives the skeleton loading state. */
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["removeBadge", "filtersChanged", "onActionClick", "chatter-click"]);

const app = getCurrentInstance();
const { proxy: instanceProxy } = app || {};
const skeletonLoadingEnabled = ref(false);

onMounted(async () => {
  // MDP-560 — gate skeleton loading behind the consistent-skeleton feature flag
  try {
    skeletonLoadingEnabled.value =
      (await instanceProxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
  } catch (error) {
    skeletonLoadingEnabled.value = false;
  }
});

const showSkeletonLoading = computed(() => props.loading && skeletonLoadingEnabled.value);

const badgeHolders = useBadgeHolders();
const viewState = useBadgeHoldersViewState();
const paginationDefaultParams = badgeHolders.paginationDefaultParams;

badgeHolders.allBadgeHolders.value = props.items;
badgeHolders.badgeHoldersCount.value = props.itemsCount || props.items.length;

watch(
  () => viewState.state.filters.values,
  (newFilters) => {
    const statusFilters = newFilters.filter((filter) =>
      ["Current", "Expired", "Terminated", "Removed"].includes(filter)
    );

    const levelNumbers = newFilters
      .map((filter) => toBadgeLevelFilterNumber(filter))
      .filter(Boolean);

    emit("filtersChanged", {
      status: statusFilters,
      level: levelNumbers,
    });
  },
  { immediate: true }
);

const handleRemoveBadge = (item) => {
  emit("removeBadge", item);
};

const handleChatterClick = (item, payload) => {
  emit("chatter-click", { item, payload });
};

const handleIssueBadge = () => {
  emit("onActionClick");
};

const handleLevelCounterFilter = (selectedLevels) => {
  const currentFilters = [...(viewState.state.filters.values || [])];
  const statusFilters = currentFilters.filter((filter) =>
    ["Current", "Expired", "Terminated", "Removed"].includes(filter)
  );
  const levels = Array.isArray(selectedLevels) ? selectedLevels : [];
  viewState.state.filters.values = [...statusFilters, ...levels];
};

const badgeLevelCountsRef = toRef(props, "badgeLevelCounts");
const itemsRef = toRef(props, "items");
const onRefreshBadgesRef = toRef(props, "onRefreshBadges");
const hideActionButtonRef = toRef(props, "hideActionButton");
const pageTypeRef = toRef(props, "pageType");
const hideBadgeRemoveIconRef = toRef(props, "hideBadgeRemoveIcon");
const expirationIconReadOnlyRef = toRef(props, "expirationIconReadOnly");
const tableConfig = useTableConfig(
  props.filterLevels,
  handleRemoveBadge,
  badgeLevelCountsRef,
  itemsRef,
  onRefreshBadgesRef,
  hideActionButtonRef,
  pageTypeRef,
  hideBadgeRemoveIconRef,
  expirationIconReadOnlyRef,
  handleChatterClick
);

const displayItems = computed(() => props.items);
const displayItemsCount = computed(() => {
  if (props.useClientPagination) {
    return props.items.length;
  }
  return props.itemsCount || props.items.length;
});
</script>

<style scoped>
::v-deep .v-list-item.with-employee {
  border: none !important;
  min-width: 250px !important;
  max-width: 290px !important;
}

::v-deep .empty-content-title,
::v-deep .empty-content-description {
  max-width: 352px !important;
}

::v-deep .table-content.empty-background-pattern {
  background-position: 50% -46px !important;
}

::v-deep .user-card .el-tooltip div {
  height: 25px !important;
}
</style>
